import React, {
    useState,
    useRef,
    useEffect,
    useCallback,
    isValidElement,
    cloneElement,
} from 'react'
import { createPortal } from 'react-dom'
import styled, { css, keyframes } from 'styled-components'
import {
    useFloating,
    offset,
    flip,
    shift,
    autoUpdate,
} from '@floating-ui/react'

export interface TooltipStyleProps {
    position?: 'top' | 'bottom' | 'left' | 'right'
    size?: 'small' | 'medium' | 'large'
    variant?: 'light' | 'dark'
    showArrow?: boolean
}

export interface TooltipProps extends TooltipStyleProps {
    content: React.ReactNode
    children: React.ReactNode
    delay?: number
    disabled?: boolean
    offset?: number
    className?: string
}

export const TooltipComponent: React.FC<TooltipProps> = ({
    content,
    children,
    delay = 300,
    disabled = false,
    offset: localOffset = 8,
    position = 'top',
    size = 'medium',
    variant = 'dark',
    showArrow = true,
    className,
    ...props
}) => {
    const [isVisible, setIsVisible] = useState(false)
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
        undefined,
    )
    const clickCooldownRef = useRef<ReturnType<typeof setTimeout> | undefined>(
        undefined,
    )

    const { refs, floatingStyles, placement } = useFloating({
        strategy: 'fixed',
        placement: position,
        middleware: [offset(localOffset), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    })

    const showTooltip = useCallback(() => {
        if (disabled) return
        if (clickCooldownRef.current) return

        timeoutRef.current = setTimeout(() => {
            setIsVisible(true)
        }, delay)
    }, [disabled, delay])

    const hideTooltip = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }
        setIsVisible(false)
    }, [])

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
            if (clickCooldownRef.current) {
                clearTimeout(clickCooldownRef.current)
            }
        }
    }, [])

    const handleMouseEnter = () => {
        showTooltip()
    }

    const handleMouseLeave = () => {
        hideTooltip()
    }

    const handleFocus = () => {
        showTooltip()
    }

    const handleBlur = () => {
        hideTooltip()
    }

    const handleMouseDown = () => {
        hideTooltip()
        if (clickCooldownRef.current) {
            clearTimeout(clickCooldownRef.current)
        }
        clickCooldownRef.current = setTimeout(() => {
            clickCooldownRef.current = undefined
        }, 500)
    }

    const getPortalContainer = useCallback(
        (element: HTMLElement | null): HTMLElement | ShadowRoot => {
            if (element) {
                const rootNode = element.getRootNode()
                if (rootNode instanceof ShadowRoot) {
                    return rootNode
                }
            }
            return document.body
        },
        [],
    )

    const [portalContainer, setPortalContainer] = useState<
        HTMLElement | ShadowRoot
    >(document.body)

    const arrowPosition = placement.split(
        '-',
    )[0] as TooltipStyleProps['position']
    const tooltipContent = isVisible ? (
        <TooltipContainer
            ref={refs.setFloating}
            position={arrowPosition}
            size={size}
            variant={variant}
            showArrow={showArrow}
            style={floatingStyles}
            className={className}
            {...props}
        >
            {content}
            {showArrow && <TooltipArrow position={arrowPosition} />}
        </TooltipContainer>
    ) : null

    const mergeRefs = useCallback(
        (node: HTMLElement | null) => {
            refs.setReference(node)

            if (node) {
                const container = getPortalContainer(node)
                setPortalContainer(container)
            }

            const childElement = children as React.ReactElement & {
                ref?: React.Ref<HTMLElement>
            }
            const originalRef = childElement.ref
            if (originalRef) {
                if (typeof originalRef === 'function') {
                    originalRef(node)
                } else if (
                    originalRef &&
                    typeof originalRef === 'object' &&
                    'current' in originalRef
                ) {
                    ;(
                        originalRef as React.MutableRefObject<HTMLElement | null>
                    ).current = node
                }
            }
        },
        [refs, children, getPortalContainer],
    )

    const mergeEventHandler = <T extends React.SyntheticEvent>(
        handler: () => void,
        existingHandler?: (e: T) => void,
    ) => {
        return (e: T) => {
            handler()
            if (existingHandler) {
                existingHandler(e)
            }
        }
    }

    type ChildProps = {
        ref?: React.Ref<HTMLElement>
        onMouseEnter?: (e: React.MouseEvent) => void
        onMouseLeave?: (e: React.MouseEvent) => void
        onFocus?: (e: React.FocusEvent) => void
        onBlur?: (e: React.FocusEvent) => void
        onMouseDown?: (e: React.MouseEvent) => void
        tabIndex?: number
    }

    const childProps: Partial<ChildProps> = {
        ref: mergeRefs,
        tabIndex: disabled ? -1 : 0,
        onMouseEnter: mergeEventHandler<React.MouseEvent>(
            handleMouseEnter,
            (children as React.ReactElement<ChildProps>).props?.onMouseEnter,
        ),
        onMouseLeave: mergeEventHandler<React.MouseEvent>(
            handleMouseLeave,
            (children as React.ReactElement<ChildProps>).props?.onMouseLeave,
        ),
        onFocus: mergeEventHandler<React.FocusEvent>(
            handleFocus,
            (children as React.ReactElement<ChildProps>).props?.onFocus,
        ),
        onBlur: mergeEventHandler<React.FocusEvent>(
            handleBlur,
            (children as React.ReactElement<ChildProps>).props?.onBlur,
        ),
        onMouseDown: mergeEventHandler<React.MouseEvent>(
            handleMouseDown,
            (children as React.ReactElement<ChildProps>).props?.onMouseDown,
        ),
    }

    if (!isValidElement(children)) {
        console.error(
            'TooltipComponent: children must be a single React element',
        )
        return <>{children}</>
    }

    const childWithProps = cloneElement(
        children as React.ReactElement<ChildProps>,
        childProps as React.Attributes & ChildProps,
    )

    return (
        <>
            {childWithProps}
            {createPortal(tooltipContent, portalContainer)}
        </>
    )
}

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
`

const getArrowPositionStyles = (
    position: TooltipStyleProps['position'] = 'top',
) => {
    switch (position) {
        case 'top':
            return css`
                top: 100%;
                left: 50%;
                transform: translateX(-50%);
                border-top-color: inherit;
                border-left-color: transparent;
                border-right-color: transparent;
                border-bottom-color: transparent;
            `
        case 'bottom':
            return css`
                bottom: 100%;
                left: 50%;
                transform: translateX(-50%);
                border-bottom-color: inherit;
                border-left-color: transparent;
                border-right-color: transparent;
                border-top-color: transparent;
            `
        case 'left':
            return css`
                left: 100%;
                top: 50%;
                transform: translateY(-50%);
                border-left-color: inherit;
                border-top-color: transparent;
                border-bottom-color: transparent;
                border-right-color: transparent;
            `
        case 'right':
            return css`
                right: 100%;
                top: 50%;
                transform: translateY(-50%);
                border-right-color: inherit;
                border-top-color: transparent;
                border-bottom-color: transparent;
                border-left-color: transparent;
            `
        default:
            return css``
    }
}

const getSizeStyles = (size: TooltipStyleProps['size'] = 'medium') => {
    switch (size) {
        case 'small':
            return css`
                // prettier-ignore
                padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) =>
                    theme.spacing[3]};
                font-size: ${({ theme }) => theme.typography.fontSize.sm};
            `
        case 'medium':
            return css`
                // prettier-ignore
                padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) =>
                    theme.spacing[4]};
                font-size: ${({ theme }) => theme.typography.fontSize.base};
            `
        case 'large':
            return css`
                // prettier-ignore
                padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) =>
                    theme.spacing[5]};
                font-size: ${({ theme }) => theme.typography.fontSize.lg};
            `
        default:
            return css``
    }
}

export const TooltipContainer = styled.div.withConfig({
    shouldForwardProp: (prop) =>
        !['position', 'size', 'variant', 'showArrow'].includes(prop),
})<TooltipStyleProps>`
    position: fixed;
    z-index: 9999;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
    line-height: ${({ theme }) => theme.typography.lineHeight.normal};
    pointer-events: none;
    animation: ${fadeIn} ${({ theme }) => theme.transitions.fast} ease-out;
    text-align: center;
    white-space: pre-line;
    max-width: 300px;
    word-wrap: break-word;
    backdrop-filter: blur(7px);
    color: ${({ theme }) => theme.colors.greyScale[50]};
    border: 1px solid ${({ theme }) => theme.colors.greyScale[200]};

    ${({ size }) => getSizeStyles(size)}
`

export const TooltipArrow = styled.div.withConfig({
    shouldForwardProp: (prop) => !['position'].includes(prop),
})<{ position?: TooltipStyleProps['position'] }>`
    position: absolute;
    width: 0;
    height: 0;
    border: 6px solid transparent;
    ${({ position }) => getArrowPositionStyles(position)}
`
